import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { ErrorModal } from '@/components/molecules/qr-checkin/error-modal'
import { fetchReservationsForSelfCheckin } from '@/apis/aipass'
import { decodeQr } from '@/libs/qr-decode'
import QrReader from 'react-qr-reader'
import { useHistory } from 'react-router-dom'
import { Footer } from '@/components/molecules/front-tablet/footer'
import { useTranslation } from 'react-i18next'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { SearchReservationTypeWhenCheckedIn } from '@/models/reservation'
import { notification } from 'antd'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

export const SmartCheckinQrReader: React.FC = props => {
  const history = useHistory()
  const { t } = useTranslation()
  const [isError, setIsError] = useState<boolean>(false)
  const [isScanCompleted, setIsScanCompleted] = useState<boolean>(false)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()

  const onScan = async data => {
    if (isScanCompleted) {
      return
    }
    if (!data) {
      setIsError(false)
      return
    }
    decodeQr(data)
      .then(async (res: any) => {
        try {
          setIsScanCompleted(true)
          const response = (await fetchReservationsForSelfCheckin({
            hotelId: state.hotelId,
            checkinId: res.id,
            isCheckin: 1,
          })) as SearchReservationTypeWhenCheckedIn[]
          saveState({
            ...state,
            searchReservation: {
              condition: { mode: 'qr' },
              result: {
                isDuplicateReservationBySameName: false,
                notCheckinReservations: undefined,
                preCheckinReservations: response,
              },
            },
            selectReservation: undefined,
          })
          history.push({ pathname: '/self-checkin/select-reservation' })
        } catch (error: any) {
          let noticeMessage = t('Reservation not found. Please try a different search method.')
          if (error.response?.status === 403) {
            noticeMessage = t(error.response.data.errorMessage, error.response.data.messageParameter)
          }
          notification.warn({ message: noticeMessage, style: { whiteSpace: 'pre-line' } })
          await new Promise(resolve => setTimeout(resolve, 3000))
          setIsScanCompleted(false)
        }
      })
      .catch(e => {
        console.log(e)
        setIsError(true)
      })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }
    setIsInitializeComponent(true)
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <SelfCheckinLayout state={state} fullscreen>
      {isError && <ErrorModal />}
      <div css={qrCameraWrapperStyle}>
        <QrReader showViewFinder={false} onScan={onScan} onError={() => setIsError(true)} constraints={{ facingMode: 'user' }} />
        <p css={qrCameraTitleStyle}>{t('Please align QR code within the frame to scan')}</p>
        <div css={qrCameraMaskStyle}>
          <div css={maskTopStyle} />
          <div css={cameraWrapperStyle}>
            <div css={maskLeftStyle} />
            <div css={qrCameraStyle} />
            <div css={maskRightStyle} />
          </div>
          <div css={maskBottomStyle} />
        </div>
        <Footer
          left={{
            label: t('Back'),
            onClick: history.goBack,
          }}
        />
      </div>
    </SelfCheckinLayout>
  )
}

const qrCameraWrapperStyle = css({
  background: '#F2F2F2',
  minWidth: '100%',
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  textAlign: 'center',
  section: {
    height: '100%',
  },
})

const qrCameraMaskStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  position: 'absolute',
  inset: 0,
})

const cameraWrapperStyle = css({
  display: 'flex',
  flexShrink: 0,
  position: 'relative',
})

const maskTopStyle = css(
  {
    background: 'rgba(0, 0, 0, 0.6)',
    height: 'calc((100vh - 90px - 525px)/2)',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { height: 'calc((100vh - 90px - 620px)/2)' },
  }),
)

const maskLeftStyle = css(
  {
    background: 'rgba(0, 0, 0, 0.6)',
    height: 525,
    width: 'calc((100% - 525px)/2)',
    left: 0,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { height: 620, width: 'calc((100% - 620px)/2)' },
  }),
)

const qrCameraStyle = css(
  {
    width: '525px',
    height: '525px',
    background: 'transparent',
    border: '2px solid #FFFFFF',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { width: 620, height: 620 },
  }),
)

const maskRightStyle = css(
  {
    background: 'rgba(0, 0, 0, 0.6)',
    height: 525,
    width: 'calc((100% - 525px)/2)',
    position: 'absolute',
    right: 0,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { height: 620, width: 'calc((100% - 620px)/2)' },
  }),
)

const maskBottomStyle = css({
  background: 'rgba(0, 0, 0, 0.6)',
  flex: 1,
})

const qrCameraTitleStyle = css(
  {
    fontSize: 18,
    letterSpacing: '3.6px',
    fontWeight: 'bold',
    lineHeight: '28px',
    color: 'white',
    position: 'absolute',
    width: '100%',
    zIndex: 2,
    top: 'calc((100vh - 90px - 525px)/4)',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 22, top: 'calc((100vh - 90px - 620px)/4)' },
  }),
)
